// #ux-redesign-start: HistoryList rediseñado (Patients).
// Antes: una sola tabla gigante con N columnas de fechas — inutilizable en
// mobile y abrumadora en desktop.
//
// Ahora:
//   - Resolve del Grupo activo via `AnalytesGroupsService` (mismo que
//     StudyAnalytes y KiLab editor). Cada analito cae en su clasificador.
//   - Render por sección (Hämatologie, Nierenfunktion, ...).
//       · Desktop (lg+): master-detail — los clasificadores como tab lateral
//         a la izquierda, los analitos del clasificador activo a la derecha.
//         Sin acordeón.
//       · Mobile / tablet: acordeón colapsable (un clasificador abierto).
//   - Por cada analito, una fila con:
//       · Identificador
//       · Rango de referencia
//       · Sparkline de los últimos N valores
//       · Último valor con badge de abnormal
//       · Toggle "Ver todo" → expande tabla con TODOS los puntos por fecha.
//   - Toolbar con: buscador + selector de Grupo (si >1) + Expandir/Colapsar
//     (este último solo en modo acordeón).
//
// i18n completo (4 idiomas) — labels via `analytes-groups` y `laboratory-detail`.
// ─────────────────────────────────────────────────────────────────────────────

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslate } from 'react-translate';
import moment from 'moment';

import LaboratoryService from '../../services/LaboratoryService';
import AnalytesGroupsService, {
  DEFAULT_GROUP_KEY,
} from 'services/AnalytesGroupsService';
import useResponsive from 'hooks/useResponsive';
import {
  Input,
  Badge,
  Spinner,
  EmptyState,
  LoadingBlock,
} from 'components/ds';

const ABNORMAL_FLAGS = ['H', 'L', 'HH', 'LL', 'A', '*'];
const isAbnormalCell = (cell) => {
  if (!cell) return false;
  if (cell.abnormal === true) return true;
  const flag = cell.flag || cell.abnormalFlag || '';
  return ABNORMAL_FLAGS.includes(String(flag).trim().toUpperCase());
};

const SearchIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="11" cy="11" r="7" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);

const ChevronIcon = ({ open }) => (
  <svg
    width="14" height="14" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
    aria-hidden="true"
    style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 150ms ease' }}
  >
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

// Sparkline mini-SVG: dibuja una línea simple con los últimos valores.
const Sparkline = ({ points, abnormalPoints }) => {
  if (!points || points.length === 0) return null;
  const width = 80;
  const height = 24;
  const pad = 2;
  const max = Math.max(...points);
  const min = Math.min(...points);
  const range = max - min || 1;
  const stepX = (width - pad * 2) / Math.max(1, points.length - 1);
  const coords = points.map((v, i) => {
    const x = pad + i * stepX;
    const y = pad + (height - pad * 2) * (1 - (v - min) / range);
    return { x, y, abnormal: !!(abnormalPoints && abnormalPoints[i]) };
  });
  const pathD = coords
    .map((c, i) => (i === 0 ? `M ${c.x.toFixed(1)} ${c.y.toFixed(1)}` : `L ${c.x.toFixed(1)} ${c.y.toFixed(1)}`))
    .join(' ');
  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      className="ds-history__spark"
      aria-hidden="true"
    >
      <path d={pathD} fill="none" stroke="currentColor" strokeWidth="1.5" />
      {coords.map((c, i) => (
        <circle
          key={i}
          cx={c.x}
          cy={c.y}
          r={c.abnormal ? 2.5 : 1.8}
          fill={c.abnormal ? 'var(--color-action-danger)' : 'currentColor'}
        />
      ))}
    </svg>
  );
};

const parseNumeric = (v) => {
  if (v == null) return null;
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  const m = String(v).replace(',', '.').match(/-?\d+(\.\d+)?/);
  if (!m) return null;
  const n = Number(m[0]);
  return Number.isFinite(n) ? n : null;
};

const HistoryList = () => {
  const t = useTranslate('history-list');
  const tDetail = useTranslate('laboratory-detail');
  const tGroups = useTranslate('analytes-groups');
  const { isDesktop } = useResponsive();

  const [laboratoryHistory, setLaboratoryHistory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const queryTrim = query.trim().toLowerCase();
  const queryActive = queryTrim.length > 0;

  const [activeGroupKey, setActiveGroupKey] = useState(DEFAULT_GROUP_KEY);
  const [group, setGroup] = useState(null);
  const [classifications, setClassifications] = useState({});
  const [resolving, setResolving] = useState(false);

  // Fetch del historial
  useEffect(() => {
    let cancelled = false;
    LaboratoryService.getStudyHistory()
      .then((res) => {
        if (cancelled) return;
        setLaboratoryHistory(res && res.data ? res.data : {});
      })
      .catch(() => !cancelled && setLaboratoryHistory({}))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, []);

  // Grupo activo: por ahora SIEMPRE el default. La agrupación no es
  // seleccionable por el paciente — a futuro la indicará el médico. Se busca
  // el grupo marcado `is_default_for_doctor`; si no hay, se usa DEFAULT_GROUP_KEY.
  useEffect(() => {
    let cancelled = false;
    AnalytesGroupsService.list()
      .then((res) => {
        if (cancelled) return;
        const list = (res && (res.data || res)) || [];
        if (!Array.isArray(list)) return;
        const def = list.find((g) => g.is_default_for_doctor);
        if (def) setActiveGroupKey(def.key);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // Lista de identifiers raw del historial.
  const rawIdentifiers = useMemo(() => {
    if (!laboratoryHistory) return [];
    return Object.keys(laboratoryHistory).map((id) => laboratoryHistory[id].rawIdentifier || laboratoryHistory[id].fullName || id);
  }, [laboratoryHistory]);

  // Resolve del Grupo
  useEffect(() => {
    if (!rawIdentifiers.length) {
      setGroup(null);
      setClassifications({});
      return;
    }
    let cancelled = false;
    setResolving(true);
    AnalytesGroupsService.resolve(rawIdentifiers, activeGroupKey)
      .then((res) => {
        if (cancelled) return;
        const data = res && (res.data || res);
        setGroup(data?.group || null);
        setClassifications(data?.classifications || {});
      })
      .catch(() => {
        if (!cancelled) { setGroup(null); setClassifications({}); }
      })
      .finally(() => !cancelled && setResolving(false));
    return () => { cancelled = true; };
  }, [rawIdentifiers, activeGroupKey]);

  // Set ordenado de fechas (todas las que aparecen en cualquier analito).
  const allDates = useMemo(() => {
    if (!laboratoryHistory) return [];
    const set = new Set();
    Object.keys(laboratoryHistory).forEach((id) => {
      (laboratoryHistory[id].data || []).forEach((c) => set.add(c.date));
    });
    return Array.from(set).sort((a, b) => String(b).localeCompare(String(a))); // desc
  }, [laboratoryHistory]);

  // Rows normalizadas + filtro de búsqueda
  const analyteRows = useMemo(() => {
    if (!laboratoryHistory) return [];
    return Object.keys(laboratoryHistory).map((id) => {
      const item = laboratoryHistory[id];
      const cellsByDate = {};
      (item.data || []).forEach((c) => { cellsByDate[c.date] = c; });
      const seriesAsc = (item.data || [])
        .slice()
        .sort((a, b) => String(a.date).localeCompare(String(b.date)));
      const values = seriesAsc.map((c) => parseNumeric(c.value)).filter((v) => v != null);
      const abnormals = seriesAsc.map((c) => isAbnormalCell(c));
      const last = seriesAsc[seriesAsc.length - 1] || null;
      return {
        id,
        rawIdentifier: item.rawIdentifier || item.fullName || id,
        // #ux-redesign-fix: `fullName` ya viene del backend con el código OBX-3
        // recortado (getLaboratoryHistory). Un segundo .replace acá recortaba
        // TAMBIÉN el nombre del analito y dejaba solo el sufijo de método
        // (ej. "i.S. (enz.)"), colapsando analitos distintos al mismo texto.
        // Solo se recorta el prefijo cuando hay que caer al `id` sintético.
        identifier: item.fullName || String(id).replace(/^([\S]{1,})\s/, ''),
        range: item.range,
        units: item.units || '',
        cellsByDate,
        sparkValues: values,
        sparkAbnormal: abnormals,
        last,
        lastAbnormal: last ? isAbnormalCell(last) : false,
      };
    }).filter((row) => {
      if (!queryTrim) return true;
      return [row.identifier, row.range, row.units, String(row.last?.value || '')]
        .some((s) => String(s).toLowerCase().includes(queryTrim));
    });
  }, [laboratoryHistory, queryTrim]);

  // Buckets por clasificador
  const sections = useMemo(() => {
    if (!group || !group.classifiers) {
      return analyteRows.length ? [{ key: '_all', label: null, order: 0, rows: analyteRows }] : [];
    }
    const fallback = group.classifiers.find((c) => c.is_fallback) || { key: 'general' };
    const buckets = {};
    for (const c of group.classifiers) buckets[c.key] = [];
    for (const row of analyteRows) {
      const ck = classifications[row.rawIdentifier] || fallback.key;
      if (!buckets[ck]) buckets[ck] = [];
      buckets[ck].push(row);
    }
    return group.classifiers
      .map((c) => ({
        key: c.key,
        label: tGroups(`${c.key}.name`),
        order: c.order,
        rows: buckets[c.key] || [],
      }))
      .filter((s) => s.rows.length > 0)
      .sort((a, b) => a.order - b.order);
  }, [analyteRows, group, classifications, tGroups]);

  // ── Estado del acordeón (mobile / tablet) ──────────────────────────────────
  // TODAS las secciones abiertas por default.
  const [openKeys, setOpenKeys] = useState(() => new Set());
  useEffect(() => {
    if (!sections.length) { setOpenKeys(new Set()); return; }
    setOpenKeys((prev) => {
      if (prev.size === 0) return new Set(sections.map((s) => s.key));
      const validKeys = new Set(sections.map((s) => s.key));
      const filtered = new Set();
      for (const k of prev) if (validKeys.has(k)) filtered.add(k);
      return filtered.size > 0 ? filtered : new Set(sections.map((s) => s.key));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sections]);
  const effectiveOpenKeys = queryActive
    ? new Set(sections.map((s) => s.key))
    : openKeys;
  const allOpen = sections.length > 0 && effectiveOpenKeys.size === sections.length;

  const toggleSection = (key) => {
    if (queryActive) return;
    setOpenKeys((prev) => {
      if (prev.size === 1 && prev.has(key)) return new Set();
      return new Set([key]);
    });
  };

  const toggleAll = () => {
    if (queryActive) return;
    setOpenKeys(allOpen ? new Set() : new Set(sections.map((s) => s.key)));
  };

  // ── Estado del master-detail (desktop) ─────────────────────────────────────
  // Clasificador activo del tab lateral. Por defecto SIEMPRE el primero.
  // Sólo se conserva una selección distinta si la eligió el usuario — así un
  // estado transitorio (ej. classifications aún vacías → todo cae a "general")
  // no puede dejar fijado el último tab. React 16 no batchea los setState del
  // .then de resolve(), por eso ese render intermedio existe.
  const userPickedSectionRef = useRef(false);
  const [selectedSectionKey, setSelectedSectionKey] = useState(null);
  useEffect(() => {
    if (!sections.length) { setSelectedSectionKey(null); return; }
    setSelectedSectionKey((prev) => {
      const stillValid = prev && sections.some((s) => s.key === prev);
      if (userPickedSectionRef.current && stillValid) return prev;
      return sections[0].key;
    });
  }, [sections]);

  // Expand-row state: muestra la tabla horizontal con todas las fechas.
  const [expandedRows, setExpandedRows] = useState(() => new Set());
  const toggleRow = (id) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // Render de una fila de analito — compartido por acordeón y master-detail.
  const renderRow = (row) => {
    const expanded = expandedRows.has(row.id);
    const hasSeries = row.sparkValues.length >= 2;
    return (
      <li
        className={`ds-history__row ${row.lastAbnormal ? 'is-abnormal' : ''} ${expanded ? 'is-expanded' : ''}`}
        key={row.id}
      >
        <button
          type="button"
          className="ds-history__row-head"
          onClick={() => toggleRow(row.id)}
          aria-expanded={expanded}
        >
          <div className="ds-history__row-main">
            <div className="ds-history__row-name">{row.identifier}</div>
            {row.range && (
              <div className="ds-history__row-range">
                {t('reference')}: {row.range} {row.units}
              </div>
            )}
          </div>
          <div className="ds-history__row-trend">
            {hasSeries && (
              <Sparkline
                points={row.sparkValues}
                abnormalPoints={row.sparkAbnormal}
              />
            )}
          </div>
          <div className="ds-history__row-last">
            {row.last ? (
              <>
                <span
                  className={`ds-history__last-value ${
                    row.lastAbnormal ? 'is-abnormal' : ''
                  }`}
                >
                  {row.last.value} {row.units}
                </span>
                {row.lastAbnormal && (
                  <Badge variant="danger" size="sm">
                    {String(row.last.flag || row.last.abnormalFlag || '!').trim().toUpperCase() || '!'}
                  </Badge>
                )}
                <small className="ds-history__last-date">
                  {moment(row.last.date, 'YYYYMMDDhhmmss').format('DD/MM/YYYY')}
                </small>
              </>
            ) : (
              <span className="ds-history__last-empty">—</span>
            )}
          </div>
          <span className="ds-history__row-cue">
            <ChevronIcon open={expanded} />
          </span>
        </button>

        {expanded && (
          <div className="ds-history__row-table-wrap">
            <table className="ds-history__row-table">
              <thead>
                <tr>
                  <th>{t('study-date')}</th>
                  <th>{tDetail('observation-value')}</th>
                  <th>{tDetail('references-range')}</th>
                  <th>{tDetail('abnormal-flags')}</th>
                </tr>
              </thead>
              <tbody>
                {allDates
                  .filter((d) => row.cellsByDate[d])
                  .map((d) => {
                    const cell = row.cellsByDate[d];
                    const abnormal = isAbnormalCell(cell);
                    return (
                      <tr
                        key={d}
                        className={
                          abnormal ? 'ds-history__row-table-row--abnormal' : ''
                        }
                      >
                        <td>{moment(d, 'YYYYMMDDhhmmss').format('DD/MM/YYYY')}</td>
                        <td>
                          {cell.value} {row.units}
                        </td>
                        <td>{row.range || '—'}</td>
                        <td>
                          {abnormal && (
                            <Badge variant="danger" size="sm">
                              {String(cell.flag || cell.abnormalFlag || '!').trim().toUpperCase() || '!'}
                            </Badge>
                          )}
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
        )}
      </li>
    );
  };

  if (loading) return <LoadingBlock />;
  if (!laboratoryHistory || analyteRows.length === 0) {
    return <EmptyState title={t('no-history')} description="" />;
  }

  const totalShown = analyteRows.length;
  const totalAbnormal = analyteRows.filter((r) => r.lastAbnormal).length;

  // Desktop con >1 clasificador → master-detail (tab lateral). Si solo hay un
  // clasificador (o el Grupo no resolvió) no tiene sentido un nav de 1 ítem.
  const useMasterDetail = isDesktop && sections.length > 1;
  const currentSection =
    sections.find((s) => s.key === selectedSectionKey) || sections[0] || null;

  return (
    <div className="ds-history">
      <div className="ds-history__toolbar">
        <Input
          name="history-search"
          placeholder={tDetail('search-analytes')}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          iconLeft={<SearchIcon />}
          aria-label={tDetail('search-analytes')}
          className="ds-history__search"
        />
        {/* Expandir/colapsar — solo en modo acordeón (mobile / tablet). */}
        {!useMasterDetail && sections.length > 1 && (
          <button
            type="button"
            className="ds-history__expand-toggle"
            onClick={toggleAll}
            disabled={queryActive}
          >
            {allOpen ? t('collapse-all') : t('expand-all')}
          </button>
        )}
        {resolving && (
          <span className="ds-history__resolving" aria-label={tDetail('loading-study')}>
            <Spinner size="sm" />
          </span>
        )}
      </div>

      <div className="ds-history__summary">
        <span>
          <strong>{totalShown}</strong> {t('studies-shown')}
        </span>
        {totalAbnormal > 0 && (
          <Badge variant="danger" size="sm">
            {totalAbnormal} {t('abnormal-count')}
          </Badge>
        )}
      </div>

      {useMasterDetail ? (
        /* ── Desktop: master-detail ─────────────────────────────────────── */
        <div className="ds-history__split">
          <nav className="ds-history__nav" aria-label={t('classifiers')}>
            {sections.map((section) => {
              const active = section.key === currentSection?.key;
              return (
                <button
                  key={section.key}
                  type="button"
                  className={`ds-history__nav-item ${active ? 'is-active' : ''}`}
                  onClick={() => {
                    userPickedSectionRef.current = true;
                    setSelectedSectionKey(section.key);
                  }}
                  aria-current={active ? 'true' : undefined}
                >
                  <span className="ds-history__nav-label">{section.label}</span>
                  <span className="ds-history__nav-count">{section.rows.length}</span>
                </button>
              );
            })}
          </nav>
          <div className="ds-history__panel">
            {currentSection && (
              <ul className="ds-history__rows" role="list">
                {currentSection.rows.map(renderRow)}
              </ul>
            )}
          </div>
        </div>
      ) : isDesktop ? (
        /* ── Desktop con un solo clasificador: lista directa, sin acordeón ─ */
        <div className="ds-history__section is-open">
          <ul className="ds-history__rows" role="list">
            {(sections[0] ? sections[0].rows : []).map(renderRow)}
          </ul>
        </div>
      ) : (
        /* ── Mobile / tablet: acordeón ──────────────────────────────────── */
        sections.map((section) => {
          const isOpen = effectiveOpenKeys.has(section.key);
          return (
            <section
              className={`ds-history__section ${isOpen ? 'is-open' : ''}`}
              key={section.key}
            >
              {section.label && (
                <button
                  type="button"
                  className="ds-history__section-head"
                  onClick={() => toggleSection(section.key)}
                  disabled={queryActive}
                  aria-expanded={isOpen}
                >
                  <h3 className="ds-history__section-title">{section.label}</h3>
                  <span className="ds-history__section-meta">
                    <span className="ds-history__section-count">
                      {section.rows.length}
                    </span>
                    <ChevronIcon open={isOpen} />
                  </span>
                </button>
              )}

              {isOpen && (
                <ul className="ds-history__rows" role="list">
                  {section.rows.map(renderRow)}
                </ul>
              )}
            </section>
          );
        })
      )}
    </div>
  );
};

export default HistoryList;
// #ux-redesign-end
